import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useApolloClient } from '@apollo/client';
import { getSelectedFamily, setSelectedFamily } from '../utils/familyScope';
import { useAuth } from './AuthContext';

// The bride/groom family whose service details are being viewed/managed — by an admin
// (any family) or a matchmaker (their clients: families they created a Service Wizard for).
export const FAMILY_MANAGER_ROLES = ['system_admin', 'matchmaker'];
const FamilyContext = createContext(null);

export function FamilyProvider({ children }) {
  const client = useApolloClient();
  const { user } = useAuth();
  const [family, setFamilyState] = useState(getSelectedFamily);

  // Login/logout clears the stored selection; keep state in sync with it
  // (a non-admin must never send a stored selection, or the API would reject it).
  useEffect(() => {
    const canManage = FAMILY_MANAGER_ROLES.includes(user?.role?.name);
    if (user && !canManage) setSelectedFamily(null);
    setFamilyState(canManage ? getSelectedFamily() : null);
  }, [user]);

  const setFamily = useCallback((next) => {
    setSelectedFamily(next);
    setFamilyState(next);
    // Cached results belong to the previous family — refetch everything for the new one
    client.resetStore().catch(() => {});
  }, [client]);

  return (
    <FamilyContext.Provider value={{ family, setFamily }}>
      {children}
    </FamilyContext.Provider>
  );
}

export function useFamily() {
  return useContext(FamilyContext);
}
